import { Injectable } from "@angular/core";
import { HttpService } from "./http.service";
import { DropdownModel, IAppointmentStatusModel, IAppointmentTypeModel, IChecklistQuestionModel, ICustomerAreaModel, ICustomCustomerFieldModel, ICustomerTypeModel, IDiscountModel, IDropdownModel, IEmailerAddressModel, IFuelSurchargeModel, IInvoiceLaborItemModel, IInvoiceStatusModel, IItemGroupingModel, IJobStatusModel, IJobTypeModel, ILathamCarrierModel, ILathamDistributorModel, IMaintenanceReadingItemModel, ILocationModel, IPaymentTermModel, IPriceLevelModel, IQuickAddCategoryModel, IRebateModel, IResourceModel, IServiceAreaModel, ISmartlistQuestionModel, ITaxRegionModel, IUserModel, ITeamModel, AppointmentStatusModel, ItemModel, IItemModel, JellyFishOrderingSettingsModel, IJobCostingSetupModel, IPOSCategoryModel } from "@models";
import { IUserResourceSortOrderModel } from "@models/users/user-resource-sort-order.model";
import { IPreloadCheckSum } from "@models/lookups/preload-check-sum.model";
import { IUserLookupModel } from "@models/lookups/user-lookup.model";
import { GlobalsService } from "./globals.service";
import { DevLogsStore } from "@stores";

enum LookupTypes {
    AppointmentStatuses, AppointmentTypes, ChecklistQuestions, CustomCustomerFields, CustomerAreas, CustomerTypes, Discounts, EmailerAddresses, FuelSurcharges, InvoiceStatuses, InvoiceLaborItems, ItemGroupings, JellyFishOrderingSettings, JobTypes, JobCostingModel, Locations, LathamCarriers, LathamDistributors, MaintenanceReadingItems, PaymentTerms, PriceLevels, QuickAddCategories, POSCategories, Rebates, Resources, ServiceAreas, SmartlistQuestions, TaxRegions, Teams, Users, JobStatuses, NewJobName, UserResourceSortOrder
};

@Injectable()
export class LookupService {
    private static lookupTypes: any = {};
    lookupTypesNames = [
        "AppointmentStatuses", "AppointmentTypes", "ChecklistQuestions", "CustomCustomerFields",
        "CustomerAreas", "CustomerTypes", "Discounts",
        "FuelSurcharges", "InvoiceLaborItems", "InvoiceStatuses", "ItemGroupings", "JellyFishOrderingSettings",
        "JobTypes", "JobStatuses", "JobCostingSetupModel", "Locations", "LathamCarriers",
        "LathamDistributors", "MaintenanceReadingItems", "PaymentTerms",
        "PriceLevels", "QuickAddCategories", "POSCategories", "Rebates", "Resources",
        "ServiceAreas", "SmartlistQuestions", "TaxRegions", "Teams",
        "Users", "UserResourceSortOrder"
    ];
    constructor(private httpService: HttpService,
        private devLogsStore: DevLogsStore) {
    }

    async preloadLookups(): Promise<void> {
        let preloadCheckSum: IPreloadCheckSum;
        if (GlobalsService.isOnline) {
            preloadCheckSum = await this.httpService.get("/lookups/getPreloadCheckSum");
            localStorage.setItem("preloadCheckSum", JSON.stringify(preloadCheckSum));
        }
        else {
            preloadCheckSum = JSON.parse(localStorage.getItem("preloadCheckSum"));
        }



        let promisesMap = new Map();

        this.lookupTypesNames.forEach(type => {
            let hashKey = `LOOKUPS_${type.toUpperCase()}_HASH`;
            let hashValue = localStorage.getItem(hashKey);
            if (GlobalsService.isOnline && (hashValue != preloadCheckSum[`${type.charAt(0).toLowerCase() + type.slice(1)}Hash`] || preloadCheckSum[`${type.charAt(0).toLowerCase() + type.slice(1)}Hash`] == null)) {
                const endpoint = `/lookups/get${type}`;
                const promise = this.httpService.get(endpoint);
                promisesMap.set(type, promise);
                localStorage.setItem(hashKey, preloadCheckSum[`${type.charAt(0).toLowerCase() + type.slice(1)}Hash`]);
            }
            else {
                const data = JSON.parse(localStorage.getItem(`LOOKUP_DATA_${type.toUpperCase()}`));
                LookupService.lookupTypes[LookupTypes[type]] = data;
            }
        });
        const keys = Array.from(promisesMap.keys());
        const promises = Array.from(promisesMap.values());
        if (keys.length > 0) {
            try {
                const results = await Promise.all(promises);
                results.forEach((result, index) => {
                    const key = keys[index];
                    console.info(`Storing data for key: ${key}`, result);
                    localStorage.setItem(`LOOKUP_DATA_${key.toUpperCase()}`, JSON.stringify(result));
                    LookupService.lookupTypes[LookupTypes[key]] = result;
                });
            }
            catch (error) {
                console.error("Error with Promise.all:", error);
            }
        }
    }

    public clearLookups() {
        this.lookupTypesNames.forEach(type => {
            const hashKey = `LOOKUPS_${type.toUpperCase()}_HASH`;
            const dataKey = `LOOKUP_DATA_${type.toUpperCase()}`;

            // Remove the hash key and data key from localStorage
            localStorage.removeItem(hashKey);
            localStorage.removeItem(dataKey);

        });
    }


    private async refreshLookup<T>(lookupType: LookupTypes): Promise<T> {
        try {
            const url = "/lookups/get" + LookupTypes[lookupType];

            LookupService.lookupTypes[lookupType] = await this.httpService.get(url);
            return Promise.resolve(LookupService.lookupTypes[lookupType]);
        } catch (error) {
            console.error(error);
            return Promise.reject(error);
        }
    }

    private async updateLookup<T>(lookupType: LookupTypes, model: T): Promise<T> {
        const url = "/lookups/update" + LookupTypes[lookupType];
        return this.httpService.post(url, model);
    }

    private getLookup<T>(lookupType: LookupTypes, idFieldName: string, id?: number, includeInactive?: boolean): T[] {
        let returnModel = <T[]>LookupService.lookupTypes[lookupType];

        if (!returnModel)
            return [];

        id = id || 0;

        if (returnModel.length === 0)
            return returnModel;

        if (!includeInactive && returnModel[0]["active"] !== undefined)
            returnModel = returnModel.filter(c => (!!c["active"] === true));
        if (id && returnModel.findIndex(c => c[idFieldName] === id) < 0) {
            const inactiveModel = (<T[]>LookupService.lookupTypes[lookupType]).find(c => c[idFieldName] === id);
            if (inactiveModel)
                returnModel.unshift(inactiveModel);
        }

        return returnModel;

    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Appointment Statuses
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshAppointmentStatuses(): Promise<IAppointmentStatusModel[]> {
        return this.refreshLookup<IAppointmentStatusModel[]>(LookupTypes.AppointmentStatuses);
    }

    public getAppointmentStatuses(includeInactive: boolean = false): IAppointmentStatusModel[] {
        return this.getLookup<IAppointmentStatusModel>(LookupTypes.AppointmentStatuses, "appointmentStatusId", null, includeInactive);
    }

    public getDefaultAppointmentStatus(): IAppointmentStatusModel {
        const defaultApptStatus = this.getLookup<IAppointmentStatusModel>(LookupTypes.AppointmentStatuses, "appointmentStatusId", null, true).find(x => x.isDefault === true);
        if (defaultApptStatus)
            return defaultApptStatus;
        else {
            const scheduledApptStatus = this.getLookup<IAppointmentStatusModel>(LookupTypes.AppointmentStatuses, "appointmentStatusId", null, true).find(x => x.description === 'Scheduled');
            return scheduledApptStatus;
        }
    }

    public getAppointmentStatus(statusId: number): IAppointmentStatusModel {
        return this.getLookup<IAppointmentStatusModel>(LookupTypes.AppointmentStatuses, "appointmentStatusId", statusId, false).find(x => x.appointmentStatusId === statusId && x.active);
    }

    public getAppointmentStatusesForDropdown(appointmentStatusId?: number): IDropdownModel[] {
        return this.getLookup<IAppointmentStatusModel>(LookupTypes.AppointmentStatuses, "appointmentStatusId", appointmentStatusId)
            .filter(x => x.active == true)
            .map(x => new DropdownModel(x.appointmentStatusId, x.description));
    }

    // Appointment statuses in workflow order, a separator line between each group (DropdownModel.dividerBefore).
    // Statuses belong to each company but the standard ones share these names. Statuses a company added itself
    // follow alphabetically. Inactive statuses are left out unless the appointment already has one.
    //
    // Scheduled, Rescheduled and Postponed were briefly hidden as retired, with New and Unscheduled standing in.
    // They aren't retired: Scheduled is the working status at nearly every company (44 of 45 active in Aug 2026,
    // 7 of them with no New status at all), and a visit copied from one in Billing could no longer be set back to
    // Scheduled or Rescheduled. A company that stops using one deactivates it like any other status.
    private static readonly appointmentStatusGroups = [
        ["New", "Scheduled", "In Transit", "At Jobsite"],
        ["Billing", "Complete", "Not Finished", "Cancelled"],
        ["Unscheduled", "Rescheduled", "Postponed", "Quote Needed", "Warranty Review"]
    ];

    public getOrderedAppointmentStatusesForDropdown(currentAppointmentStatusId?: number): IDropdownModel[] {
        const normalize = (description: string) => (description || "").trim().toLowerCase();
        const statuses = this.getLookup<IAppointmentStatusModel>(LookupTypes.AppointmentStatuses, "appointmentStatusId", currentAppointmentStatusId)
            .filter(x => x.active == true || x.appointmentStatusId === currentAppointmentStatusId);

        const standardNames = LookupService.appointmentStatusGroups.reduce((all, group) => all.concat(group), [] as string[]).map(normalize);

        const groups: IAppointmentStatusModel[][] = LookupService.appointmentStatusGroups
            .map(group => group
                .map(name => statuses.find(x => normalize(x.description) === normalize(name)))
                .filter(x => !!x));

        groups.push(statuses
            .filter(x => !standardNames.includes(normalize(x.description)))
            .sort((a, b) => (a.description || "").localeCompare(b.description || "")));

        const items: IDropdownModel[] = [];
        groups
            .filter(group => group.length > 0)
            .forEach(group => group.forEach((status, index) => {
                const item: IDropdownModel = new DropdownModel(status.appointmentStatusId, status.description);
                item.dividerBefore = index === 0 && items.length > 0;
                items.push(item);
            }));

        return items;
    }

    public async updateAppointmentStatus(appointmentStatusModel: IAppointmentStatusModel): Promise<IAppointmentStatusModel> {
        const returnModel = await this.updateLookup(LookupTypes.AppointmentStatuses, appointmentStatusModel);
        await this.refreshAppointmentStatuses();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Appointment Types
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshAppointmentTypes(): Promise<IAppointmentTypeModel[]> {
        return this.refreshLookup<IAppointmentTypeModel[]>(LookupTypes.AppointmentTypes);
    }

    public getAppointmentTypes(appointmentTypeId?: number, includeInactive?: boolean): IAppointmentTypeModel[] {
        return this.getLookup<IAppointmentTypeModel>(LookupTypes.AppointmentTypes, "appointmentTypeId", appointmentTypeId, includeInactive);
    }

    public getAppointmentTypesForDropdown(appointmentTypeId?: number): IDropdownModel[] {
        return this.getLookup<IAppointmentTypeModel>(LookupTypes.AppointmentTypes, "appointmentTypeId", appointmentTypeId).map(x => new DropdownModel(x.appointmentTypeId, x.description));
    }

    public async updateAppointmentType(appointmentTypeModel: IAppointmentTypeModel): Promise<IAppointmentTypeModel> {
        const returnModel = await this.httpService.post("/lookups/updateAppointmentType", appointmentTypeModel);

        await this.refreshAppointmentTypes();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Checklist Questions
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshChecklistQuestion(): Promise<IChecklistQuestionModel[]> {
        return this.refreshLookup<IChecklistQuestionModel[]>(LookupTypes.ChecklistQuestions);
    }

    public getChecklistQuestions(checklistQuestionId?: number, includeInactive?: boolean): IChecklistQuestionModel[] {
        return this.getLookup<IChecklistQuestionModel>(LookupTypes.ChecklistQuestions, "checklistQuestionId", checklistQuestionId, includeInactive);
    }

    public getChecklistQuestionsForDropdown(checklistQuestionId?: number): IDropdownModel[] {
        return this.getLookup<IChecklistQuestionModel>(LookupTypes.ChecklistQuestions, "checklistQuestionId", checklistQuestionId).map(x => new DropdownModel(x.checklistQuestionId, x.sortOrder));
    }

    public async updateChecklistQuestion(checklistQuestionModel: IChecklistQuestionModel): Promise<IChecklistQuestionModel> {
        const returnModel = await this.updateLookup(LookupTypes.ChecklistQuestions, checklistQuestionModel);
        await this.refreshChecklistQuestion();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Custom Customer Fields
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshCustomCustomerFields(): Promise<ICustomCustomerFieldModel[]> {
        return this.refreshLookup<ICustomCustomerFieldModel[]>(LookupTypes.CustomCustomerFields);
    }

    public getCustomCustomerFields(customCustomerFieldId?: number, includeInactive?: boolean): ICustomCustomerFieldModel[] {
        return this.getLookup<ICustomCustomerFieldModel>(LookupTypes.CustomCustomerFields, "customCustomerFieldId", customCustomerFieldId, includeInactive);
    }

    public async updateCustomCustomerFields(customCustomerFieldModel: ICustomCustomerFieldModel): Promise<ICustomCustomerFieldModel> {
        const returnModel = await this.updateLookup(LookupTypes.CustomCustomerFields, customCustomerFieldModel);
        await this.refreshCustomCustomerFields();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Customer Areas
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshCustomerAreas(): Promise<ICustomerAreaModel[]> {
        return this.refreshLookup<ICustomerAreaModel[]>(LookupTypes.CustomerAreas);
    }

    public getCustomerAreas(customerAreaId?: number, includeInactive?: boolean): ICustomerAreaModel[] {
        return this.getLookup<ICustomerAreaModel>(LookupTypes.CustomerAreas, "customerAreaId", customerAreaId, includeInactive);
    }

    public getCustomerAreasForDropdown(customerAreaId?: number): IDropdownModel[] {
        return this.getLookup<ICustomerAreaModel>(LookupTypes.CustomerAreas, "customerAreaId", customerAreaId).map(x => new DropdownModel(x.customerAreaId, x.name));
    }

    public async updateCustomerAreas(customerAreaModels: ICustomerAreaModel[]): Promise<ICustomerAreaModel[]> {
        const returnModels = await this.updateLookup(LookupTypes.CustomerAreas, customerAreaModels);
        await this.refreshCustomerAreas();
        return returnModels;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Customer Types
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshCustomerTypes(): Promise<ICustomerTypeModel[]> {
        return this.refreshLookup<ICustomerTypeModel[]>(LookupTypes.CustomerTypes);
    }

    public getCustomerTypes(customerTypeId?: number, includeInactive?: boolean): ICustomerTypeModel[] {
        return this.getLookup<ICustomerTypeModel>(LookupTypes.CustomerTypes, "customerTypeId", customerTypeId, includeInactive);
    }

    public getCustomerTypesForDropdown(customerTypeId?: number): IDropdownModel[] {
        return this.getLookup<ICustomerTypeModel>(LookupTypes.CustomerTypes, "customerTypeId", customerTypeId).map(x => new DropdownModel(x.customerTypeId, x.name));
    }

    public async updateCustomerTypes(customerTypeModels: ICustomerTypeModel[]): Promise<ICustomerTypeModel[]> {
        const returnModels = await this.updateLookup(LookupTypes.CustomerTypes, customerTypeModels);
        await this.refreshCustomerTypes();
        return returnModels;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Discounts
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshDiscounts(): Promise<IDiscountModel[]> {
        return this.refreshLookup<IDiscountModel[]>(LookupTypes.Discounts);
    }

    public getDiscounts(discountId?: number, includeInactive?: boolean): IDiscountModel[] {
        return this.getLookup<IDiscountModel>(LookupTypes.Discounts, "discountId", discountId, includeInactive);
    }

    public getDiscountsForDropdown(discountId?: number): IDropdownModel[] {
        return this.getLookup<IDiscountModel>(LookupTypes.Discounts, "discountId", discountId).map(x => new DropdownModel(x.discountId, x.sku));
    }

    public async updateDiscounts(discountModels: IDiscountModel[]): Promise<IDiscountModel[]> {
        LookupService.lookupTypes[LookupTypes.Discounts] = await this.httpService.post("/lookups/updateDiscounts", discountModels);
        return this.getDiscounts();
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Email Address Strings
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshEmailerAddresses(): Promise<IEmailerAddressModel[]> {
        return this.refreshLookup<IEmailerAddressModel[]>(LookupTypes.EmailerAddresses);
    }

    public getEmailerAddresses(): IEmailerAddressModel[] {
        return LookupService.lookupTypes[LookupTypes.EmailerAddresses];
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Fuel Surcharges
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshFuelSurcharges(): Promise<IFuelSurchargeModel[]> {
        return this.refreshLookup<IFuelSurchargeModel[]>(LookupTypes.FuelSurcharges);
    }

    public getFuelSurcharges(): IFuelSurchargeModel[] {
        return this.getLookup<IFuelSurchargeModel>(LookupTypes.FuelSurcharges, "fuelSurchargesId");
    }

    public async updateFuelSurcharges(fuelSurchargeModels: IFuelSurchargeModel[]): Promise<IFuelSurchargeModel[]> {
        const returnModel = <IFuelSurchargeModel[]>(await this.httpService.post("/lookups/updateFuelSurcharges", fuelSurchargeModels));
        await this.refreshFuelSurcharges();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Invoice Labor Item
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshInvoiceLaborItems(): Promise<IInvoiceLaborItemModel[]> {
        return this.refreshLookup<IInvoiceLaborItemModel[]>(LookupTypes.InvoiceLaborItems);
    }

    public getInvoiceLaborItems(invoiceLaborItemId?: number, includeInactive?: boolean): IInvoiceLaborItemModel[] {
        return this.getLookup<IInvoiceLaborItemModel>(LookupTypes.InvoiceLaborItems, "invoiceLaborItemId", invoiceLaborItemId, includeInactive);
    }

    public getInvoiceLaborItemsForDropdown(invoiceLaborItemId?: number): IDropdownModel[] {
        return this.getLookup<IInvoiceLaborItemModel>(LookupTypes.InvoiceLaborItems, "invoiceLaborItemId", invoiceLaborItemId).map(x => new DropdownModel(x.invoiceLaborItemId, x.sku));
    }

    public async updateInvoiceLaborItems(invoiceLaborItemModels: IInvoiceLaborItemModel[]): Promise<IInvoiceLaborItemModel[]> {
        LookupService.lookupTypes[LookupTypes.InvoiceLaborItems] = await this.httpService.post("/lookups/updateInvoiceLaborItems", invoiceLaborItemModels);
        return this.getInvoiceLaborItems();
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Invoice Statuses
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshInvoiceStatuses(): Promise<IInvoiceStatusModel[]> {
        return this.refreshLookup<IInvoiceStatusModel[]>(LookupTypes.InvoiceStatuses);
    }

    public getInvoiceStatuses(invoiceStatusId?: number, includeInactive?: boolean): IInvoiceStatusModel[] {
        return this.getLookup<IInvoiceStatusModel>(LookupTypes.InvoiceStatuses, "invoiceStatusId", invoiceStatusId, includeInactive);
    }

    public getInvoiceStatusesForDropdown(invoiceStatusId?: number): IDropdownModel[] {
        return this.getLookup<IInvoiceStatusModel>(LookupTypes.InvoiceStatuses, "invoiceStatusId", invoiceStatusId).map(x => new DropdownModel(x.invoiceStatusId, x.name));
    }

    public async updateInvoiceStatuses(invoiceStatusModels: IInvoiceStatusModel[]): Promise<IInvoiceStatusModel[]> {
        const returnModels = await this.updateLookup(LookupTypes.InvoiceStatuses, invoiceStatusModels);
        await this.refreshInvoiceStatuses();
        return returnModels;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // ItemGroupings
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshItemGroupings(): Promise<IItemGroupingModel[]> {
        return this.refreshLookup<IItemGroupingModel[]>(LookupTypes.ItemGroupings);
    }

    public getItemGroupings(itemGroupingId?: number, includeInactive?: boolean): IItemGroupingModel[] {
        return this.getLookup<IItemGroupingModel>(LookupTypes.ItemGroupings, "itemGroupingId", itemGroupingId, includeInactive);
    }

    public getItemGroupingsForDropdown(itemGroupingId?: number): IDropdownModel[] {
        return this.getLookup<IItemGroupingModel>(LookupTypes.ItemGroupings, "itemGroupingId", itemGroupingId).map(x => new DropdownModel(x.itemGroupingId, x.description));
    }

    public async updateItemGroupings(itemGroupingModel: IItemGroupingModel): Promise<IItemGroupingModel> {
        const returnModel = await this.updateLookup(LookupTypes.ItemGroupings, itemGroupingModel);
        await this.refreshItemGroupings();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Job Types
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshJobTypes(): Promise<IJobTypeModel[]> {
        return this.refreshLookup<IJobTypeModel[]>(LookupTypes.JobTypes);
    }

    public getJobTypes(jobTypeId?: number, includeInactive?: boolean): IJobTypeModel[] {
        return this.getLookup<IJobTypeModel>(LookupTypes.JobTypes, "jobTypeId", jobTypeId, includeInactive);
    }

    public getJobTypesForDropdown(jobTypeId?: number): IDropdownModel[] {
        return this.getLookup<IJobTypeModel>(LookupTypes.JobTypes, "jobTypeId", jobTypeId).map(x => new DropdownModel(x.jobTypeId, x.description));
    }

    public async updateJobTypes(jobTypeModels: IJobTypeModel[]): Promise<IJobTypeModel[]> {
        const returnModels = await this.updateLookup(LookupTypes.JobTypes, jobTypeModels);
        await this.refreshJobTypes();
        return returnModels;
    }


    ////////////////////////////////////////////////////////////////////////////////////////////////
    // JellyFish 
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public getJellyFishOrderingSettings(): JellyFishOrderingSettingsModel {
        return LookupService.lookupTypes[LookupTypes.JellyFishOrderingSettings];
    }

    public updateJellyFishOrderingSettings(settings: JellyFishOrderingSettingsModel) {
        LookupService.lookupTypes[LookupTypes.JellyFishOrderingSettings] = settings;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Job Statuses
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshJobStatuses(): Promise<IJobStatusModel[]> {
        return this.refreshLookup<IJobStatusModel[]>(LookupTypes.JobStatuses);
    }

    public getJobStatuses(jobStatusId?: number, includeInactive?: boolean): IJobStatusModel[] {
        return this.getLookup<IJobStatusModel>(LookupTypes.JobStatuses, "jobStatusId", jobStatusId, includeInactive);
    }

    public getJobStatusesForDropdown(jobTypeId?: number): IDropdownModel[] {
        return this.getLookup<IJobStatusModel>(LookupTypes.JobStatuses, "jobStatusId", jobTypeId).map(x => new DropdownModel(x.jobStatusId, x.description));
    }

    public async updateJobStatus(jobStatusModel: IJobStatusModel): Promise<IJobStatusModel> {
        const returnModel = await this.updateLookup(LookupTypes.JobStatuses, jobStatusModel);
        await this.refreshJobStatuses();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Job Costing
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public getJobCostingSetupModel(): IJobCostingSetupModel {
        return LookupService.lookupTypes[LookupTypes["JobCostingSetupModel"]];
    }

    public updateJobCostingSetupModel(jobCostingSetupModel: IJobCostingSetupModel) {
        LookupService.lookupTypes[LookupTypes["JobCostingSetupModel"]] = jobCostingSetupModel;
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Locations
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshLocations(): Promise<ILocationModel[]> {
        return this.refreshLookup<ILocationModel[]>(LookupTypes.Locations);
    }

    public getLocations(locationId?: number, includeInactive?: boolean): ILocationModel[] {
        return this.getLookup<ILocationModel>(LookupTypes.Locations, "locationId", locationId, includeInactive);
    }

    public getLocationsForDropdown(locationId?: number): IDropdownModel[] {
        return this.getLookup<ILocationModel>(LookupTypes.Locations, "locationId", locationId).map(x => new DropdownModel(x.locationId, x.name));
    }

    public async updateLocation(locationModel: ILocationModel): Promise<ILocationModel> {
        const returnModel = await this.updateLookup(LookupTypes.Locations, locationModel);
        await this.refreshLocations();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Latham Carriers
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public getLathamCarriers(lathamCarrierId?: number, includeInactive?: boolean): ILathamCarrierModel[] {
        return this.getLookup<ILathamCarrierModel>(LookupTypes.LathamCarriers, "lathamCarrierId", lathamCarrierId, includeInactive);
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Latham Distributors
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public getLathamDistributors(): ILathamDistributorModel[] {
        return <ILathamDistributorModel[]>LookupService.lookupTypes[LookupTypes.LathamDistributors];
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Maintenance Reading Items
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public getMaintenanceReadingItems(): IMaintenanceReadingItemModel[] {
        return <IMaintenanceReadingItemModel[]>LookupService.lookupTypes[LookupTypes.MaintenanceReadingItems];
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // New Job Name
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public async getNewJobName(): Promise<string> {
        return this.httpService.get('/companies/getNewJobName');
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Payment Terms
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshPaymentTerms(): Promise<IPaymentTermModel[]> {
        return this.refreshLookup<IPaymentTermModel[]>(LookupTypes.PaymentTerms);
    }

    public getPaymentTerms(paymentTermId?: number, includeInactive?: boolean): IPaymentTermModel[] {
        return this.getLookup<IPaymentTermModel>(LookupTypes.PaymentTerms, "paymentTermId", paymentTermId, includeInactive);
    }

    public getPaymentTermsForDropdown(paymentTermId?: number): IDropdownModel[] {
        return this.getLookup<IPaymentTermModel>(LookupTypes.PaymentTerms, "paymentTermId", paymentTermId).map(x => new DropdownModel(x.paymentTermId, x.name));
    }

    public async updatePaymentTerm(paymentTermModel: IPaymentTermModel): Promise<IPaymentTermModel> {
        const returnModel = await this.updateLookup(LookupTypes.PaymentTerms, paymentTermModel);
        await this.refreshPaymentTerms();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Price Levels
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshPriceLevels(): Promise<IPriceLevelModel[]> {
        return this.refreshLookup<IPriceLevelModel[]>(LookupTypes.PriceLevels);
    }

    public getPriceLevels(priceLevelId?: number, includeInactive?: boolean): IPriceLevelModel[] {
        return this.getLookup<IPriceLevelModel>(LookupTypes.PriceLevels, "priceLevelId", priceLevelId, includeInactive);
    }

    public getPriceLevelsForDropdown(priceLevelId?: number): IDropdownModel[] {
        return this.getLookup<IPriceLevelModel>(LookupTypes.PriceLevels, "priceLevelId", priceLevelId, false).map(x => new DropdownModel(x.priceLevelId, x.name));
    }

    public async updatePriceLevel(priceLevelModel: IPriceLevelModel): Promise<IPriceLevelModel> {
        const returnModel = await this.updateLookup(LookupTypes.PriceLevels, priceLevelModel);
        await this.refreshPriceLevels();
        return returnModel;
    }


    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Quick Add Categories
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshQuickAddCategories(): Promise<IQuickAddCategoryModel[]> {
        return this.refreshLookup<IQuickAddCategoryModel[]>(LookupTypes.QuickAddCategories);
    }

    public getQuickAddCategories(quickAddCategoryId?: number, includeInactive?: boolean): IQuickAddCategoryModel[] {
        return this.getLookup<IQuickAddCategoryModel>(LookupTypes.QuickAddCategories, "quickAddCategoryId", quickAddCategoryId, includeInactive);
    }

    public getQuickAddCategoriesForRole(quickAddCategoryId?: number, includeInactive?: boolean): IQuickAddCategoryModel[] {
        let quickAdds = this.getQuickAddCategories(quickAddCategoryId, includeInactive);
        if (GlobalsService.userInfo.roleTypeId <= 3) {
            quickAdds = quickAdds.filter(x => x.showOnAdmin);
        }
        else {
            quickAdds = quickAdds.filter(x => x.showOnServiceTech);
        }
        return quickAdds;
    }

    public getQuickAddCategoriesForDropdown(quickAddCategoryId?: number): IDropdownModel[] {
        return this.getLookup<IQuickAddCategoryModel>(LookupTypes.QuickAddCategories, "quickAddCategoryId", quickAddCategoryId).map(x => new DropdownModel(x.quickAddCategoryId, x.categoryName));
    }

    public async updateQuickAddCategories(quickAddCategoryModel: IQuickAddCategoryModel): Promise<IQuickAddCategoryModel> {
        const returnModel = await this.updateLookup(LookupTypes.QuickAddCategories, quickAddCategoryModel);
        await this.refreshQuickAddCategories();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // POS Categories
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshPOSCategories(): Promise<IPOSCategoryModel[]> {
        return this.refreshLookup<IPOSCategoryModel[]>(LookupTypes.POSCategories);
    }

    public getPOSCategories(posCategoryId?: number, includeInactive?: boolean): IPOSCategoryModel[] {
        return this.getLookup<IPOSCategoryModel>(LookupTypes.POSCategories, "posCategoryId", posCategoryId, includeInactive);
    }

    public getPOSCategoriesForDropdown(posCategoryId?: number): IDropdownModel[] {
        return this.getLookup<IPOSCategoryModel>(LookupTypes.POSCategories, "posCategoryId", posCategoryId).map(x => new DropdownModel(x.posCategoryId, x.categoryName));
    }

    public async updatePOSCategories(posCategoryModel: IPOSCategoryModel): Promise<IPOSCategoryModel> {
        const returnModel = await this.updateLookup(LookupTypes.POSCategories, posCategoryModel);
        await this.refreshPOSCategories();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Rebates
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshRebates(): Promise<IRebateModel[]> {
        return this.refreshLookup<IRebateModel[]>(LookupTypes.Rebates);
    }

    public getRebates(rebateId?: number, includeInactive?: boolean): IRebateModel[] {
        return this.getLookup<IRebateModel>(LookupTypes.Rebates, "rebateId", rebateId, includeInactive);
    }

    public getRebatesForDropdown(rebateId?: number): IDropdownModel[] {
        return this.getLookup<IRebateModel>(LookupTypes.Rebates, "rebateId", rebateId).map(x => new DropdownModel(x.rebateId, x.name));
    }

    public async updateRebates(rebateModels: IRebateModel[]): Promise<IRebateModel[]> {
        LookupService.lookupTypes[LookupTypes.Rebates] = await this.httpService.post("/lookups/updateRebates", rebateModels);
        return this.getRebates();
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Resources
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshResources(): Promise<IResourceModel[]> {
        return this.refreshLookup<IResourceModel[]>(LookupTypes.Resources);
    }

    public getResources(resourceId?: number, includeInactive?: boolean): IResourceModel[] {
        return this.getLookup<IResourceModel>(LookupTypes.Resources, "resourceId", resourceId, includeInactive);
    }

    public getResourcesForDropdown(resourceId?: number): IDropdownModel[] {
        return this.getLookup<IResourceModel>(LookupTypes.Resources, "resourceId", resourceId).map(x => new DropdownModel(x.resourceId, x.resourceName));
    }

    public getAvailableResources(resourceId: number) {
        if (GlobalsService.company.locations !== null && GlobalsService.company.locations.length > 1) {
            return this.getResources(resourceId, false)
                .filter(r => (GlobalsService.userInfo.allowedLocationIds.findIndex(l => l === r.locationId) >= 0));
        }
        else {
            return this.getResources(resourceId, false);
        }
    }

    public getAvailableResourcesForDropdown(resourceId: number) {
        return this.getAvailableResources(resourceId).map(r => new DropdownModel(r.resourceId, r.resourceName));
    }

    public async updateResources(resourceModel: IResourceModel, forLocationsSetup = false): Promise<IResourceModel> {
        const queryParams = forLocationsSetup ? "?forLocationsSetup=true" : "";
        const returnModel = await this.httpService.post(`/lookups/updateResources${queryParams}`, resourceModel);
        await this.refreshResources();
        return returnModel;
    }


    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Service Areas
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshServiceAreas(): Promise<IServiceAreaModel[]> {
        return this.refreshLookup<IServiceAreaModel[]>(LookupTypes.ServiceAreas);
    }

    public getServiceAreas(serviceAreaId?: number, includeInactive?: boolean): IServiceAreaModel[] {
        return this.getLookup<IServiceAreaModel>(LookupTypes.ServiceAreas, "serviceAreaId", serviceAreaId, includeInactive);
    }

    public getServiceAreasForDropdown(serviceAreaId?: number): IDropdownModel[] {
        return this.getLookup<IServiceAreaModel>(LookupTypes.ServiceAreas, "serviceAreaId", serviceAreaId).map(x => new DropdownModel(x.serviceAreaId, x.name));
    }

    public async updateServiceArea(serviceAreaModel: IServiceAreaModel): Promise<IServiceAreaModel> {
        let returnModel = await this.updateLookup(LookupTypes.ServiceAreas, serviceAreaModel);
        await this.refreshServiceAreas();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Smartlist Questions
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshSmartlistQuestions(): Promise<ISmartlistQuestionModel[]> {
        return this.refreshLookup<ISmartlistQuestionModel[]>(LookupTypes.SmartlistQuestions);
    }

    public getSmartlistQuestions(SmartlistQuestionId?: number, includeInactive?: boolean): ISmartlistQuestionModel[] {
        return this.getLookup<ISmartlistQuestionModel>(LookupTypes.SmartlistQuestions, "SmartlistQuestionId", SmartlistQuestionId, includeInactive);
    }

    public getSmartlistQuestionsForDropdown(SmartlistQuestionId?: number): IDropdownModel[] {
        return this.getLookup<ISmartlistQuestionModel>(LookupTypes.SmartlistQuestions, "SmartlistQuestionId", SmartlistQuestionId).map(x => new DropdownModel(x.smartlistQuestionId, x.question));
    }

    public async updateSmartlistQuestions(SmartlistQuestionModels: ISmartlistQuestionModel[]): Promise<ISmartlistQuestionModel[]> {
        const returnModels = await this.updateLookup(LookupTypes.SmartlistQuestions, SmartlistQuestionModels);
        await this.refreshSmartlistQuestions();
        return returnModels;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Tax Regions
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshTaxRegions(): Promise<ITaxRegionModel[]> {
        return this.refreshLookup<ITaxRegionModel[]>(LookupTypes.TaxRegions);
    }

    public getTaxRegions(TaxRegionId?: number, includeInactive?: boolean): ITaxRegionModel[] {
        return this.getLookup<ITaxRegionModel>(LookupTypes.TaxRegions, "TaxRegionId", TaxRegionId, includeInactive);
    }

    public getTaxRegionsForDropdown(TaxRegionId?: number): IDropdownModel[] {
        return this.getLookup<ITaxRegionModel>(LookupTypes.TaxRegions, "TaxRegionId", TaxRegionId).map(x => new DropdownModel(x.taxRegionId, x.regionName));
    }

    public async updateTaxRegions(taxRegionModels: ITaxRegionModel[]): Promise<ITaxRegionModel[]> {
        LookupService.lookupTypes[LookupTypes.TaxRegions] = await this.httpService.post("/lookups/updateTaxRegions", taxRegionModels);
        return this.getTaxRegions();
    }

    public async findCustomersWithTaxRegion(taxRegionId: number) {
        return await this.httpService.get(`/lookups/FindCustomersWithTaxRegion?taxRegionId=${taxRegionId}`);
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Users
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshUsers(): Promise<IUserModel[]> {
        return this.refreshLookup<IUserModel[]>(LookupTypes.Users);
    }

    public refreshUserLookups(): Promise<IUserLookupModel[]> {
        return this.refreshLookup<IUserLookupModel[]>(LookupTypes.Users);
    }

    public getUsers(userId?: number, includeInactive?: boolean): IUserLookupModel[] {
        return this.getLookup<IUserLookupModel>(LookupTypes.Users, "userId", userId, includeInactive);
    }

    /** Active crew members who can see this location (Visible Locations). Includes already-assigned user ids. */
    public getCrewMembersForLocation(locationId: number, includeUserIds: number[] = []): IUserLookupModel[] {
        const includeSet = new Set(includeUserIds ?? []);

        return this.getUsers(null, true).filter(user => {
            if (!user.active) {
                return false;
            }
            if (includeSet.has(user.userId)) {
                return true;
            }
            if (user.isCrewMember !== true) {
                return false;
            }
            if (!locationId) {
                return true;
            }

            return (user.locationIds ?? []).includes(locationId);
        });
    }

    /** Trucks/resources for one or more departments (locations). */
    public getResourcesForLocations(locationIds: number[] | null | undefined, includeResourceId?: number): IResourceModel[] {
        const ids = (locationIds ?? []).filter(id => !!id);
        if (ids.length === 0) {
            return [];
        }
        const idSet = new Set(ids);
        let resources = (this.getResources(null, true) ?? [])
            .filter(r => r.locationId != null && idSet.has(r.locationId) && r.active !== false);

        if (includeResourceId) {
            const inactiveAssigned = (this.getResources(includeResourceId, true) ?? [])
                .find(r => r.resourceId === includeResourceId && r.locationId != null && idSet.has(r.locationId));
            if (inactiveAssigned && !resources.some(r => r.resourceId === includeResourceId)) {
                resources = [...resources, inactiveAssigned];
            }
        }
        return resources.sort((a, b) => {
            const orderA = a.sortOrder ?? 999;
            const orderB = b.sortOrder ?? 999;
            if (orderA !== orderB) {
                return orderA - orderB;
            }
            return (a.resourceName ?? "").localeCompare(b.resourceName ?? "");
        });
    }

    public getResourcesForLocation(locationId: number | null | undefined, includeResourceId?: number): IResourceModel[] {
        return this.getResourcesForLocations(locationId ? [locationId] : [], includeResourceId);
    }

    public getResourcesForLocationDropdown(locationId: number | null | undefined, includeResourceId?: number): IDropdownModel[] {
        return this.getResourcesForLocation(locationId, includeResourceId)
            .map(r => new DropdownModel(r.resourceId, r.resourceName));
    }

    public getResourcesForLocationsDropdown(locationIds: number[] | null | undefined, includeResourceId?: number): IDropdownModel[] {
        return this.getResourcesForLocations(locationIds, includeResourceId)
            .map(r => new DropdownModel(r.resourceId, r.resourceName));
    }

    public getUsersForDropdown(userId?: number): IDropdownModel[] {
        return this.getLookup<IUserLookupModel>(LookupTypes.Users, "userId", userId).map(x => new DropdownModel(x.userId, x.fullName));
    }

    public async updateUser(UserModel: IUserModel): Promise<IUserModel> {
        const returnModel = await this.updateLookup(LookupTypes.Users, UserModel);
        await this.refreshUsers();
        return returnModel;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Users Resource Sort Order
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public refreshUserResourceSortOrder(): Promise<IUserModel[]> {
        return this.refreshLookup<IUserModel[]>(LookupTypes.UserResourceSortOrder);
    }

    public getUserResourceSortOrder(userId?: number): IUserResourceSortOrderModel[] {
        return this.getLookup<IUserResourceSortOrderModel>(LookupTypes.UserResourceSortOrder, "userId", userId);
    }

    public async updateUserResourceSortOrder(userId: number, model: IUserResourceSortOrderModel[]) {
        LookupService.lookupTypes[LookupTypes.UserResourceSortOrder] = await this.httpService.post("/lookups/updateUserResourceSortOrder", model);
        return this.getUserResourceSortOrder(userId);
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Teams
    ////////////////////////////////////////////////////////////////////////////////////////////////
    public getTeamsForDropDown(): IDropdownModel[] {
        return this.getLookup<ITeamModel>(LookupTypes.Teams, "teamId").map(x => new DropdownModel(x.teamId, x.teamName));
    }

    public getTeams(): ITeamModel[] {
        return this.getLookup<ITeamModel>(LookupTypes.Teams, "teamId");
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Time Zones
    ////////////////////////////////////////////////////////////////////////////////////////////////

    public getTimeZonesForDropDown(): IDropdownModel[] {

        const timeZones = [
            new DropdownModel('Atlantic Standard Time', 'Atlantic Standard Time'),
            new DropdownModel('Central Standard Time', 'Central Standard Time'),
            new DropdownModel('Eastern Standard Time', 'Eastern Standard Time'),
            new DropdownModel('Hawaiian Standard Time', 'Hawaiian Standard Time'),
            new DropdownModel('Mountain Standard Time', 'Mountain Standard Time'),
            new DropdownModel('Pacific Standard Time', 'Pacific Standard Time')
        ]

        return timeZones;

    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // States
    ////////////////////////////////////////////////////////////////////////////////////////////////

    public getStatesForDropDown(): IDropdownModel[]{
        return this.stateList.map(x => new DropdownModel(x.id, x.text));
    }
    public stateList = [
        { text: "Alabama", id: "AL" },
        { text: "Alaska", id: "AK" },
        { text: "Arizona", id: "AZ" },
        { text: "Arkansas", id: "AR" },
        { text: "California", id: "CA" },
        { text: "Colorado", id: "CO" },
        { text: "Connecticut", id: "CT" },
        { text: "Delaware", id: "DE" },
        { text: "Florida", id: "FL" },
        { text: "Georgia", id: "GA" },
        { text: "Hawaii	", id: "HI" },
        { text: "Idaho", id: "ID" },
        { text: "Illinois", id: "IL" },
        { text: "Indiana", id: "IN" },
        { text: "Iowa", id: "IA" },
        { text: "Kansas", id: "KS" },
        { text: "Kentucky", id: "KY" },
        { text: "Louisiana", id: "LA" },
        { text: "Maine", id: "ME" },
        { text: "Maryland", id: "MD" },
        { text: "Massachusetts", id: "MA" },
        { text: "Michigan", id: "MI" },
        { text: "Minnesota", id: "MN" },
        { text: "Mississippi", id: "MS" },
        { text: "Missouri", id: "MO" },
        { text: "Montana", id: "MT" },
        { text: "Nebraska", id: "NE" },
        { text: "Nevada", id: "NV" },
        { text: "New Hampshire", id: "NH" },
        { text: "New Jersey", id: "NJ" },
        { text: "New Mexico", id: "NM" },
        { text: "New York", id: "NY" },
        { text: "North Carolina", id: "NC" },
        { text: "North Dakota", id: "ND" },
        { text: "Ohio", id: "OH" },
        { text: "Oklahoma", id: "OK" },
        { text: "Oregon", id: "OR" },
        { text: "Pennsylvania", id: "PA" },
        { text: "Rhode Island", id: "RI" },
        { text: "South Carolina", id: "SC" },
        { text: "South Dakota", id: "SD" },
        { text: "Tennessee", id: "TN" },
        { text: "Texas", id: "TX" },
        { text: "Utah", id: "UT" },
        { text: "Vermont", id: "VT" },
        { text: "Virginia", id: "VA" },
        { text: "Washington", id: "WA" },
        { text: "West Virginia", id: "WV" },
        { text: "Wisconsin", id: "WI" },
        { text: "Wyoming", id: "WY" }
    ];

    public getCanadaProvincesForDropDown(): IDropdownModel[] {
        return this.canadaProvinceList.map(x => new DropdownModel(x.id, x.text));
    }

    public canadaProvinceList = [
        { text: "Alberta", id: "AB" },
        { text: "British Columbia", id: "BC" },
        { text: "Manitoba", id: "MB" },
        { text: "New Brunswick", id: "NB" },
        { text: "Newfoundland and Labrador", id: "NL" },
        { text: "Northwest Territories", id: "NT" },
        { text: "Nova Scotia", id: "NS" },
        { text: "Nunavut", id: "NU" },
        { text: "Ontario", id: "ON" },
        { text: "Prince Edward Island", id: "PE" },
        { text: "Quebec", id: "QC" },
        { text: "Saskatchewan", id: "SK" },
        { text: "Yukon", id: "YT" }
    ];

}




